import { apiService } from './api';
import { ApiResponse, PaginatedResponse } from '../types';

export interface CompanyUserNote {
  _id?: string;
  content: string;
  action: 'permission_update' | 'role_change' | 'department_change' | 'status_change' | 'profile_update' | 'manual_note' | 'other';
  changedBy: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    avatar?: string;
  } | string;
  metadata?: {
    oldValue?: any;
    newValue?: any;
    fieldChanged?: string;
  };
  createdAt: string;
}

export interface CompanyUser {
  _id: string;
  userId: string | {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    avatar?: string;
    isActive: boolean;
  };
  companyId: string | {
    _id: string;
    name: string;
    logo?: string;
  };
  role: 'admin' | 'owner' | 'manager' | 'accountant' | 'designer' | 'maintenance_coordinator' | 'field_staff' | 'sales_agent' | 'customer_support';
  permissions: {
    module: string;
    actions: string[];
  }[];
  featurePermissions?: string[];
  notes?: CompanyUserNote[];
  designation?: string;
  department: 'Management' | 'Operations' | 'Finance' | 'Marketing' | 'Maintenance' | 'HR' | 'IT';
  joinedAt: string;
  leftAt?: string;
  salary?: {
    amount: number;
    currency: string;
    frequency: 'monthly' | 'yearly';
  };
  reporting?: {
    reportsTo?: string;
    subordinates?: string[];
  };
  workSchedule?: {
    workingDays: string[];
    workingHours: {
      start: string;
      end: string;
    };
    timezone: string;
  };
  contactPreferences?: {
    primaryPhone?: string;
    alternatePhone?: string;
    emergencyContact?: {
      name: string;
      phone: string;
      relationship: string;
    };
  };
  performance?: {
    lastReviewDate?: string;
    nextReviewDate?: string;
    rating?: number;
    goals?: {
      title: string;
      description: string;
      deadline: string;
      status: 'pending' | 'in_progress' | 'completed' | 'overdue';
    }[];
  };
  isActive: boolean;
  isPrimary: boolean;
  invitationStatus: 'pending' | 'accepted' | 'declined' | 'expired';
  invitedBy?: string;
  invitedAt?: string;
  acceptedAt?: string;
  createdAt: string;
  updatedAt: string;
  userDetails?: any;
  companyDetails?: any;
}

export interface CompanyUserFilters {
  companyIds?: string[];
  role?: string;
  department?: string;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CompanyUserLimited {
  _id: string;
  companyId: string;
  userId?:string;
  name: string;
  email: string;
  phone: string;
}

export const companyUserService = {
  // Get company users with filters
  async getCompanyUsers(filters?: CompanyUserFilters): Promise<PaginatedResponse<CompanyUser>> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (key === 'companyIds' && Array.isArray(value)) {
            value.forEach(id => params.append('companyIds[]', id));
          } else {
            params.append(key, String(value));
          }
        }
      });
    }

    const response = await apiService.get(`/company-users?${params.toString()}`);
    return response.data;
  },

  // Get single company user
  async getCompanyUser(id: string): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.get(`/company-users/${id}`);
    return response.data;
  },

  // Get users for specific company
  async getUsersByCompany(companyId: string): Promise<ApiResponse<CompanyUser[]>> {
    const params = new URLSearchParams();
    params.append('companyId', companyId);

    const response = await apiService.get(`/company-users/by-company?${params.toString()}`);
    return response.data;
  },

  // Get limited company users data (name, email, _id, companyId)
  async getCompanyUsersLimited(params?: {
    isActive?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<CompanyUserLimited>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const response = await apiService.get(`/company-users/get-limited?${queryParams.toString()}`);
    return response.data;
  },

  // Update company user
  async updateCompanyUser(id: string, data: Partial<CompanyUser>): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.put(`/company-users/${id}`, data);
    return response.data;
  },

  // Update company user permissions
  async updateUserPermissions(id: string, permissions: string[]): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.put(`/company-users/${id}/permissions`, { permissions });
    return response.data;
  },

  // Update company user role
  async updateUserRole(id: string, role: CompanyUser['role']): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.put(`/company-users/${id}/role`, { role });
    return response.data;
  },

  // Add performance goal
  async addPerformanceGoal(id: string, goal: NonNullable<NonNullable<CompanyUser['performance']>['goals']>[0]): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.post(`/company-users/${id}/goals`, goal);
    return response.data;
  },

  // Update performance goal
  async updatePerformanceGoal(id: string, goalId: string, updates: Partial<NonNullable<NonNullable<CompanyUser['performance']>['goals']>[0]>): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.put(`/company-users/${id}/goals/${goalId}`, updates);
    return response.data;
  },

  // Deactivate company user
  async deactivateCompanyUser(id: string): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.put(`/company-users/${id}/deactivate`);
    return response.data;
  },

  // Reactivate company user
  async reactivateCompanyUser(id: string): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.put(`/company-users/${id}/reactivate`);
    return response.data;
  },

  // Remove company user from company
  async removeCompanyUser(id: string): Promise<ApiResponse<{ removedUserId: string; userDetails: any; message: string }>> {
    const response = await apiService.delete(`/company-users/${id}`);
    return response.data;
  },

  // Get company user statistics
  async getCompanyUserStats(companyId: string): Promise<ApiResponse<{
    totalUsers: number;
    activeUsers: number;
    byRole: Record<string, number>;
    byDepartment: Record<string, number>;
  }>> {
    const response = await apiService.get(`/companies/${companyId}/users/stats`);
    return response.data;
  },

  // Invite user to company
  async inviteUserToCompany(data: {
    email: string;
    companyId: string;
    role: CompanyUser['role'];
    department?: string;
    designation?: string;
    message?: string;
  }): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.post('/company-users/invite', data);
    return response.data;
  },

  // Resend invitation
  async resendInvitation(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await apiService.post(`/company-users/${id}/resend-invitation`);
    return response.data;
  },

  // Cancel invitation
  async cancelInvitation(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await apiService.delete(`/company-users/${id}/cancel-invitation`);
    return response.data;
  },

  // Get company user notes
  async getCompanyUserNotes(
    id: string,
    params?: {
      page?: number;
      limit?: number;
      action?: string;
    }
  ): Promise<PaginatedResponse<CompanyUserNote>> {
    const response = await apiService.get(`/company-users/${id}/notes`, { params });
    return response.data;
  },

  // Add manual note
  async addManualNote(id: string, content: string): Promise<ApiResponse<CompanyUser>> {
    const response = await apiService.post(`/company-users/${id}/notes`, { content });
    return response.data;
  }
};